<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  getTriggers,
  deleteSubscriptionManagement
} from '@/app/core/services/subscriptions-management'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableCalendar = ref(null)
const subscriptionForDelete = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  { name: 'name', title: t('subscriptions.list.name'), table: 'name' },
  {
    name: 'endpoint',
    title: t('subscriptions.list.endpoint'),
    table: 'endpoint'
  },
  {
    name: 'eventsName',
    title: t('subscriptions.list.events'),
    table: 'events'
  },
  {
    name: 'description',
    title: t('subscriptions.list.descriptionList'),
    table: 'description'
  },
  {
    name: 'updated_at',
    title: t('subscriptions.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const CreateSubscriptionManagement = () => {
  void router.push({ name: 'CreateSubscriptionManagement' })
}
const UpdateSubscriptionManagement = (id: any) => {
  void router.push({ name: 'UpdateSubscriptionManagement', params: { id } })
}

/* Eliminar suscripción */
const removeSubscriptionManagement = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('subscriptions.list.actionStatus', {
    subscription: values.name as string
  })
  descriptionModal.value = t('modal.description')
  showModal.value = true
  subscriptionForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteSubscriptionManagement(subscriptionForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('subscriptions.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void subscriptions()
    })
    .catch(async (error: any) => {
      notificationError(error, 'subscriptions', t, notifications, loading)
    })
}

/* Metodo para cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

const subscriptions = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getTriggers(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'subscriptions', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()">
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('subscriptions.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('subscriptions.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="subscriptions" />
          <DBtn
            @click.prevent="subscriptions"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="CreateSubscriptionManagement()"
            v-if="store.hasPermission('trigger:create')">
            {{
              t('form.button.create', {
                form: t('subscriptions.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableCalendar"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), subscriptions()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), subscriptions()">
      <template #eventsName="data: any">
        <div>
          {{ data.events.toString() }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('trigger:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="UpdateSubscriptionManagement(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('trigger:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeSubscriptionManagement(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
